import { apiBaseUrl } from '@/platform/config'

// On native (Capacitor) the WebView origin is `https://localhost`, so a bare
// `/api/...` path resolves against the WebView itself and fails. `apiBaseUrl()`
// returns `VITE_API_BASE_URL` on native and `''` on web (same-origin Rails).
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: Record<string, unknown>) => void
          prompt: (callback: (notification: { isNotDisplayed: () => boolean; getNotDisplayedReason: () => string }) => void) => void
        }
      }
    }
    AppleID?: {
      auth: {
        init: (config: Record<string, unknown>) => void
        signIn: () => Promise<{
          authorization: {
            id_token: string
            code: string
          }
          user?: {
            name?: { firstName?: string; lastName?: string }
            email?: string
          }
        }>
      }
    }
  }
}

export function loadGoogleSDK(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts) {
      resolve()
      return
    }
    const script = document.createElement('script')
    script.src = 'https://accounts.google.com/gsi/client'
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load Google SDK'))
    document.head.appendChild(script)
  })
}

export function googleSignIn(clientId: string): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!window.google?.accounts) {
      reject(new Error('Google SDK not loaded'))
      return
    }
    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: (response: { credential?: string }) => {
        if (response.credential) {
          resolve(response.credential)
        } else {
          reject(new Error('No credential received from Google'))
        }
      },
    })
    window.google.accounts.id.prompt((notification) => {
      if (notification.isNotDisplayed()) {
        reject(new Error(`Google prompt not displayed: ${notification.getNotDisplayedReason()}`))
      }
    })
  })
}

export function loadAppleSDK(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.AppleID) {
      resolve()
      return
    }
    const script = document.createElement('script')
    script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js'
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load Apple SDK'))
    document.head.appendChild(script)
  })
}

export async function appleSignIn(
  serviceId: string,
  redirectUri: string
): Promise<{ idToken: string; firstName?: string; lastName?: string }> {
  if (!window.AppleID) {
    throw new Error('Apple SDK not loaded')
  }

  window.AppleID.auth.init({
    clientId: serviceId,
    scope: 'name email',
    redirectURI: redirectUri,
    usePopup: true,
  })

  const response = await window.AppleID.auth.signIn()

  return {
    idToken: response.authorization.id_token,
    firstName: response.user?.name?.firstName,
    lastName: response.user?.name?.lastName,
  }
}

export async function socialLogin(
  provider: string,
  idToken: string,
  userInfo?: { firstName?: string; lastName?: string }
): Promise<{ success: boolean; data?: Record<string, unknown>; error?: string }> {
  const res = await fetch(`${apiBaseUrl()}/api/v1/auth/social`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ provider, id_token: idToken, user_info: userInfo }),
  })
  const data = await res.json()
  if (data.success) {
    return { success: true, data: data.data }
  }
  return { success: false, error: data.error || 'Social login failed' }
}
